import React, { useContext, useEffect, useState } from 'react'
import { IPInfo } from './interface'
import {
  ProfileInfo,
  Button,
  ProfileInfoActions,
  ProfileInfoAttr,
  ProfileInfoAvatar,
  ProfileInfoAttrItem,
  ProfileInfoAttrItemIcon,
  ProfileInfoAttrItemInfo,
  Loaders,
} from './style'
import Avatar from '@berlitzplatforms/micro.ui.avatar-legacy'
import LoveIt from '@berlitz/streamline-icons/lib/bold/22-Social-Medias-Rewards-Rating/05-Love-it/LoveIt'
import LoveItAlt from '@berlitz/streamline-icons/lib/bold/22-Social-Medias-Rewards-Rating/05-Love-it/LoveItAlternate'
import { FormattedMessage } from 'react-intl'
import MicrophoneSingMan from '@berlitz/streamline-icons/lib/regular/14-Music-Audio/12-Microphone/MicrophoneSingMan'
import MessagesBubbleSquareText from '@berlitz/streamline-icons/lib/regular/21-Messages-Chat-Smileys/02-Messages-Speech-Bubbles/MessagesBubbleSquareText'
import SchoolTeacher from '@berlitz/streamline-icons/lib/regular/36-School-Learning/01-School/SchoolTeacher'
import Disable from '@berlitz/streamline-icons/lib/bold/01-Interface-Essential/23-Delete/Disable'
import InstructorProfile from '@classes/InstructorProfile'
import { FMIStudentProfileContext, useBlockedInstructors, useFave } from '../context'
import { sortBy } from 'lodash'
import Skeleton from 'react-loading-skeleton'
import Spacer from '@berlitz/spacer'
import { useRouter } from 'next/router'
import BlockModal from '../BlockModal'

const Info: React.FC<IPInfo> = ({ loading, instructor, updateBlockedInstructors = () => {} }) => {
  const router = useRouter()
  const fave = useFave()
  const blocked = useBlockedInstructors()
  const { isAdmin, baseRoute, getRouteParams } = useContext(FMIStudentProfileContext)
  const [Profile, setProfile] = useState<InstructorProfile | undefined>(undefined)
  const [isFaved, setIsFaved] = useState(true)
  const [openBlockModal, setOpenBlockModal] = useState(true)

  useEffect(() => {
    setProfile(new InstructorProfile(instructor))
    setIsFaved(fave?.isFaved(instructor?.InstructorId))
  }, [instructor, fave.faveInstructors])

  return (
    <>
      <ProfileInfo>
        <div className="profile-info">
          <ProfileInfoAvatar>
            <Avatar
              img={Profile?.Avatar}
              size="132px"
              grayedDefault
              type={'profile'}
              isLoading={loading}
              loadingType="skeleton"
            />
          </ProfileInfoAvatar>

          {!loading && (
            <div className="profile-attrs">
              <ProfileInfoActions
                isFaved={isFaved}
                isFaveDisabled={!fave.enableActions}
                isBlockDisabled={!blocked.enableActions}
              >
                {!isAdmin ? (
                  <div
                    onClick={() => {
                      if (fave.enableActions) {
                        const newFaves = fave.faveInstructors.filter(
                          ({ InstructorId }) => InstructorId !== instructor?.InstructorId
                        )

                        if (instructor) {
                          if (isFaved) {
                            fave.setFaveInstructors(newFaves)
                            fave.deleteRelatedInstructors({
                              variables: fave.getPayload('delete', [instructor]),
                            })
                            setIsFaved(false)
                          } else {
                            fave.setFaveInstructors(sortBy([...newFaves, instructor], ['Name']))
                            fave.putRelatedInstructors({
                              variables: fave.getPayload('put', [instructor]),
                            })
                            setIsFaved(true)
                          }
                        }
                      }
                    }}
                  >
                    {isFaved ? <LoveIt /> : <LoveItAlt />}
                  </div>
                ) : null}

                {blocked.allowed ? (
                  <div
                    className={`block-icon ${blocked.isBlocked(instructor?.InstructorId) ? 'blocked' : ''}`}
                    onClick={() => {
                      if (blocked.enableActions) {
                        if (blocked.isBlocked(instructor?.InstructorId) && instructor) {
                          blocked.unblockInstructors([instructor])
                          updateBlockedInstructors('unblock', [instructor])
                        } else {
                          blocked.setSelectedInstructor(instructor)
                          updateBlockedInstructors('block', [instructor!])
                          setOpenBlockModal(true)
                        }
                      }
                    }}
                  >
                    <Disable />
                  </div>
                ) : null}
                <Button
                  onClick={() => {
                    router.push(`${baseRoute}/availability?${getRouteParams()}`)
                  }}
                  disabled={blocked.isBlocked(instructor?.InstructorId)}
                >
                  <FormattedMessage id="Book instructor" defaultMessage="Book instructor" />
                </Button>
              </ProfileInfoActions>

              <ProfileInfoAttr>
                <ProfileInfoAttrItem>
                  <ProfileInfoAttrItemIcon>
                    <MicrophoneSingMan />
                  </ProfileInfoAttrItemIcon>
                  <ProfileInfoAttrItemInfo>
                    <FormattedMessage id="Speaks" defaultMessage="Speaks" /> <strong>{Profile?.PrimaryLanguage}</strong>
                  </ProfileInfoAttrItemInfo>
                </ProfileInfoAttrItem>

                {Profile?.OtherLanguages && (
                  <ProfileInfoAttrItem>
                    <ProfileInfoAttrItemIcon>
                      <MessagesBubbleSquareText />
                    </ProfileInfoAttrItemIcon>
                    <ProfileInfoAttrItemInfo>
                      <FormattedMessage id="Also speaks" defaultMessage="Also speaks" />{' '}
                      <strong>{Profile?.OtherLanguages}</strong>
                    </ProfileInfoAttrItemInfo>
                  </ProfileInfoAttrItem>
                )}

                {Profile?.TeachingSince && (
                  <ProfileInfoAttrItem>
                    <ProfileInfoAttrItemIcon>
                      <SchoolTeacher />
                    </ProfileInfoAttrItemIcon>
                    <ProfileInfoAttrItemInfo>
                      <FormattedMessage id="Teaching with Berlitz since" defaultMessage="Teaching with Berlitz since" />{' '}
                      <strong>{Profile?.TeachingSince}</strong>
                    </ProfileInfoAttrItemInfo>
                  </ProfileInfoAttrItem>
                )}
              </ProfileInfoAttr>
            </div>
          )}
        </div>

        {loading && (
          <>
            <Loaders>
              <Skeleton width="40px" height="24px" />
              <Skeleton width="100px" height="24px" />
            </Loaders>

            <Loaders>
              <Skeleton width="40px" height="24px" />
              <Skeleton width="100px" height="24px" />
            </Loaders>

            <Loaders>
              <Skeleton width="40px" height="24px" />
              <Skeleton width="100px" height="24px" />
            </Loaders>

            <Spacer />

            <Loaders>
              <Skeleton width="144px" height="24px" />
            </Loaders>
            <Loaders>
              <Skeleton width="154px" height="24px" />
            </Loaders>
            <Loaders>
              <Skeleton width="124px" height="24px" />
            </Loaders>
          </>
        )}
      </ProfileInfo>

      <div className="block-modal">
        <BlockModal
          open={openBlockModal && !!blocked.selectedInstructor}
          onClose={() => {
            blocked.setSelectedInstructor(undefined)
            setOpenBlockModal(false)
          }}
          onCompleted={() => {}}
        />
      </div>
    </>
  )
}

export default Info
